import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Info, AlertCircle } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  useDeliveryProjects,
  useDeliveryProjectTimeline,
  deliveryStatus,
  progressLabel,
  progressCaption,
  type DeliveryStatusTone,
} from "@/hooks/useDeliveryProjects";
import { useLinearProjects, useLinearIssues } from "@/hooks/useLinear";
import { DeliveryIssues } from "@/components/admin/DeliveryIssues";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatBRL } from "@/lib/format";

const TONE_CLASSES: Record<DeliveryStatusTone, string> = {
  neutral: "bg-muted text-muted-foreground",
  warn: "bg-warn-soft text-warn",
  ok: "bg-ok-soft text-ok",
};

const fmt = (v: number) => formatBRL(v);

// Linear has no FK to client_projects (confirmed — no such column exists in
// the schema), so a project's Linear issues are matched here by exact,
// case/whitespace-normalized project name. This is a best-effort, transparent
// linking rule on two REAL names — not a fabricated number — but it can miss
// a match if the names differ between the two systems, in which case the
// "Issues do projeto" panel below shows an honest empty state rather than
// guessing. It's also intentionally exact-match-only (no fuzzy matching): a
// loose match risks silently attributing another project's issues to this
// one, which is worse than showing nothing.
function normalizeName(s: string) {
  return s.trim().toLowerCase();
}

export function DeliveryProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: projects = [], isLoading, error } = useDeliveryProjects();
  const project = projects.find((p) => p.id === id);

  const { data: timeline = [], isLoading: timelineLoading } = useDeliveryProjectTimeline(project);

  const { data: linearProjectsData } = useLinearProjects();
  // Guards against a second mis-attribution risk: if two Linear projects
  // normalize to the same name, .find() would silently pick the first one
  // and the admin would see another project's issues with no indication.
  // ambiguousMatch flags that case so the UI can degrade to an honest empty
  // state instead of guessing which of the two is "right".
  const { matchedLinearProject, ambiguousMatch } = useMemo(() => {
    if (!project) return { matchedLinearProject: null, ambiguousMatch: false };
    const linearProjects = linearProjectsData?.projects?.nodes ?? [];
    const candidates = linearProjects.filter(
      (lp) => normalizeName(lp.name) === normalizeName(project.title),
    );
    if (candidates.length > 1) return { matchedLinearProject: null, ambiguousMatch: true };
    return { matchedLinearProject: candidates[0] ?? null, ambiguousMatch: false };
  }, [linearProjectsData, project]);

  // Note: when there's no name match, useLinearIssues(null) still fetches
  // the workspace-wide issue list (the hook has no `enabled` passthrough to
  // skip the request) — the result is simply unused below in that case.
  const { data: matchedIssuesData } = useLinearIssues(matchedLinearProject?.id ?? null);
  const matchedIssues = matchedLinearProject ? (matchedIssuesData?.issues?.nodes ?? []) : [];
  const openIssues = matchedIssues.filter(
    (i) => i.state.type !== "completed" && i.state.type !== "canceled",
  );
  const urgentOpenCount = openIssues.filter((i) => i.priority === 1).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <div className="flex items-start gap-3 rounded-2xl border border-bad/25 bg-card p-6 shadow-card">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-bad" />
          <div>
            <p className="text-sm font-medium">Não foi possível carregar este projeto.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {error instanceof Error ? error.message : "Tente novamente em instantes."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-16 text-center">
        <p className="text-sm text-muted-foreground">Projeto não encontrado.</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/admin/projetos")}>
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Voltar para Projetos
        </Button>
      </div>
    );
  }

  const status = deliveryStatus(project);

  return (
    <div className="mx-auto flex max-w-[1220px] flex-col gap-5">
      <div className="flex items-start gap-3.5">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-[10px]"
          aria-label="Voltar"
          onClick={() => navigate("/admin/projetos")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="font-display text-[25px] font-bold tracking-[-0.03em]">
              {project.title}
              {project.client_name ? ` — ${project.client_name}` : ""}
            </h1>
            <Badge
              className={`h-6 rounded-full border-transparent px-2.5 text-[11.5px] font-bold ${TONE_CLASSES[status.tone]}`}
            >
              {status.label}
            </Badge>
          </div>
          <p className="mt-[5px] text-[13px] text-muted-foreground">
            {project.description ?? "Sem descrição."}
            {project.due_date &&
              ` · entrega prevista ${format(new Date(project.due_date), "dd/MM/yyyy", { locale: ptBR })}`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-4">
        <div className="rounded-[14px] border border-border bg-card px-5 py-[18px] shadow-card">
          <div className="text-xs font-medium text-muted-foreground">Progresso</div>
          <div className="mt-[9px] font-display text-2xl font-bold tracking-[-0.035em]">
            {progressLabel(project)}
          </div>
          <div className="mt-[11px] h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: project.progress === null ? "0%" : `${project.progress}%` }}
            />
          </div>
          {progressCaption(project) && (
            <p className="mt-1.5 text-[11px] text-muted-foreground">{progressCaption(project)}</p>
          )}
        </div>

        <div className="rounded-[14px] border border-border bg-card px-5 py-[18px] shadow-card">
          <div className="text-xs font-medium text-muted-foreground">Issues abertas</div>
          <div className="mt-[9px] font-display text-2xl font-bold tracking-[-0.035em]">
            {matchedLinearProject ? openIssues.length : "—"}
          </div>
          <div className="mt-[5px] text-[11.5px] font-semibold text-muted-foreground">
            {ambiguousMatch ? (
              "Nome ambíguo no Linear"
            ) : !matchedLinearProject ? (
              "Sem projeto correspondente no Linear"
            ) : urgentOpenCount > 0 ? (
              <span className="text-bad">
                {urgentOpenCount} urgente{urgentOpenCount !== 1 ? "s" : ""}
              </span>
            ) : (
              "Nenhuma urgente"
            )}
          </div>
        </div>

        <div className="rounded-[14px] border border-border bg-card px-5 py-[18px] shadow-card">
          <div className="text-xs font-medium text-muted-foreground">Horas apontadas</div>
          <div className="mt-[9px] font-display text-2xl font-bold tracking-[-0.035em]">
            {project.logged_hours ?? 0}h
          </div>
          <div className="mt-[5px] text-[11.5px] text-muted-foreground">
            {project.estimated_hours
              ? `de ${project.estimated_hours}h estimadas`
              : "Sem estimativa definida"}
          </div>
        </div>

        <div className="rounded-[14px] border border-border bg-card px-5 py-[18px] shadow-card">
          <div className="text-xs font-medium text-muted-foreground">Faturado</div>
          {project.hasBudget ? (
            <>
              <div className="mt-[9px] font-display text-2xl font-bold tracking-[-0.035em]">
                {fmt(project.invoicedAmount)}
              </div>
              <div className="mt-[5px] text-[11.5px] text-muted-foreground">
                de {fmt(project.contractedAmount)} contratados
              </div>
            </>
          ) : (
            <>
              <div className="mt-[9px] font-display text-2xl font-bold tracking-[-0.035em] text-muted-foreground">
                —
              </div>
              <div className="mt-[5px] text-[11.5px] text-muted-foreground">
                Nenhum orçamento vinculado
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_1.4fr]">
        <section className="rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
          <h2 className="mb-[18px] text-[15px] font-bold">Linha do tempo</h2>
          {timelineLoading && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!timelineLoading && timeline.length === 0 && (
            <EmptyState
              size="sm"
              title="Nenhum evento registrado ainda."
              className="py-2 text-left"
            />
          )}
          {!timelineLoading && timeline.length > 0 && (
            <ol className="space-y-0">
              {timeline.map((ev, idx) => (
                <li
                  key={`${ev.kind}-${ev.date}-${idx}`}
                  className="flex gap-[13px] pb-[18px] last:pb-0"
                >
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <span className="mt-[3px] h-[11px] w-[11px] shrink-0 rounded-full border-2 border-primary bg-card" />
                    {idx < timeline.length - 1 && <span className="w-px flex-1 bg-border" />}
                  </div>
                  <div className="min-w-0 pb-1">
                    <div className="text-[13px] font-semibold leading-snug">{ev.label}</div>
                    <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                      {format(new Date(ev.date), "dd/MM/yyyy", { locale: ptBR })}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="flex items-center justify-between border-b border-border px-[22px] pb-3.5 pt-[18px]">
            <h2 className="text-[15px] font-bold">Issues do projeto</h2>
            <span className="text-xs text-muted-foreground">
              {matchedIssues.length} item{matchedIssues.length !== 1 ? "s" : ""}
            </span>
          </div>
          {matchedLinearProject ? (
            <div className="p-4">
              <DeliveryIssues lockedProjectId={matchedLinearProject.id} />
            </div>
          ) : (
            <div className="flex items-start gap-2.5 p-6 text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="text-xs">
                {ambiguousMatch ? (
                  <>
                    Mais de um projeto do Linear tem o nome "{project.title}" — o vínculo automático
                    é ambíguo, então nenhum foi escolhido.
                  </>
                ) : (
                  <>
                    Nenhum projeto do Linear com o mesmo nome ("{project.title}") foi encontrado —
                    não há vínculo automático entre projetos de cliente e projetos do Linear.
                  </>
                )}{" "}
                Veja todas as issues na aba{" "}
                <button
                  className="font-medium underline"
                  onClick={() => navigate("/admin/projetos?tab=issues")}
                >
                  Issues
                </button>
                .
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
